import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { getEnabledBuildingTypes } from '../api/enabledBuildingTypesApi';
import { BuildingType } from '../types/BuildingTypeEnums';
import { BuildingTypeLabelOption, buildingTypeLabelOptions, offeredBuildingTypes } from '../Helpers/buildingTypeOptions';

/**
 * The instance's enabled building types: undefined while loading, null when
 * the instance has none configured (absent node = every type off).
 */
export function useEnabledBuildingTypes(): BuildingType[] | null | undefined {
    const [enabled, setEnabled] = useState<BuildingType[] | null | undefined>(undefined);

    useEffect(() => {
        let cancelled = false;
        getEnabledBuildingTypes().then(types => {
            if (!cancelled) setEnabled(types);
        });
        return () => { cancelled = true; };
    }, []);

    return enabled;
}

/**
 * The types a building-type filter offers: the enabled ones plus every type a
 * building still has, in display order. Memoize `typesInUse` in the caller.
 */
export function useBuildingTypeFilterTypes(typesInUse: readonly (BuildingType | undefined)[]): BuildingType[] {
    const enabled = useEnabledBuildingTypes();
    return useMemo(() => offeredBuildingTypes(enabled ?? null, typesInUse), [enabled, typesInUse]);
}

export interface BuildingTypeFilter {
    options: BuildingTypeLabelOption[];
    /** The stored selection limited to `options`. */
    selected: BuildingType[];
    /** Sets the visible selection; hidden types keep their stored state, so a type enabled later starts selected. */
    setSelected: (types: BuildingType[]) => void;
}

/**
 * A building-type multi-select over a stored selection that may hold types
 * the filter doesn't offer. Those match no building, so they never change
 * the result; they are only kept out of view and out of the counts.
 */
export function useBuildingTypeFilter(
    stored: BuildingType[],
    setStored: Dispatch<SetStateAction<BuildingType[]>>,
    typesInUse: readonly (BuildingType | undefined)[],
): BuildingTypeFilter {
    const types = useBuildingTypeFilterTypes(typesInUse);
    const options = useMemo(() => buildingTypeLabelOptions(types), [types]);
    const selected = useMemo(() => stored.filter(type => types.includes(type)), [stored, types]);
    const setSelected = useCallback((next: BuildingType[]) => {
        setStored(previous => [...next, ...previous.filter(type => !types.includes(type))]);
    }, [setStored, types]);
    return { options, selected, setSelected };
}
